<template>
  <v-dialog v-model="dialog" max-width="600px" persistent>
    <v-card>
      <v-card-title>
        <span class="text-h6">Pošaljite UBL dokument</span>
      </v-card-title>

      <v-form ref="form" v-model="valid" @submit.prevent="sendDocument">
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-file-input
                  v-model="selectedFile"
                  label="Odaberite XML datoteku"
                  accept=".xml"
                  :rules="[rules.required, rules.xmlFile]"
                  prepend-icon="mdi-file-xml"
                  show-size
                  @change="onFileSelected"
                />
              </v-col>

              <v-col cols="12" v-if="xmlContent">
                <v-textarea
                  v-model="xmlContent"
                  label="XML sadržaj"
                  rows="6"
                  readonly
                  outlined
                  class="mb-3"
                />
              </v-col>

              <v-col cols="12">
                <v-text-field
                  v-model="documentId"
                  label="ID dokumenta (neobavezno)"
                  hint="Automatski generirani ako nije unesen"
                  persistent-hint
                />
              </v-col>

              <v-col cols="12">
                <v-textarea
                  v-model="notes"
                  label="Napomene (neobavezno)"
                  rows="3"
                  auto-grow
                  counter="500"
                  :rules="[rules.maxLength]"
                />
              </v-col>

              <v-col cols="12">
                <v-switch
                  v-model="validateOnly"
                  label="Samo validiraj dokument (ne šalje)"
                  hint="Koristite za provjeru valjanosti dokumenta prije slanja"
                  persistent-hint
                />
              </v-col>
            </v-row>
          </v-container>

          <v-alert
            v-if="validationResult"
            :type="validationResult.success ? 'success' : 'error'"
            class="mt-4"
          >
            <div v-if="validationResult.success">
              <strong>Dokument je valjan!</strong>
              <div v-if="validationResult.message">{{ validationResult.message }}</div>
            </div>
            <div v-else>
              <strong>Greška validacije:</strong>
              <div>{{ validationResult.message }}</div>
              <ul v-if="validationResult.errors && validationResult.errors.length">
                <li v-for="error in validationResult.errors" :key="error">{{ error }}</li>
              </ul>
            </div>
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="dialog = false"
            :disabled="loading"
          >
            Odustani
          </v-btn>
          <v-btn
            color="primary"
            type="submit"
            :loading="loading"
            :disabled="!valid"
          >
            {{ validateOnly ? 'Validiraj' : 'Pošalji' }}
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'SendDocumentDialog',
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      valid: false,
      loading: false,
      selectedFile: null,
      xmlContent: '',
      documentId: '',
      notes: '',
      validateOnly: false,
      validationResult: null,
      rules: {
        required: value => !!value || 'Ovo polje je obavezno',
        xmlFile: value => {
          if (!value) return true
          return value.type === 'text/xml' || value.name.endsWith('.xml') || 'Datoteka mora biti XML format'
        },
        maxLength: value => !value || value.length <= 500 || 'Maksimalno 500 znakova'
      }
    }
  },
  computed: {
    dialog: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    }
  },
  watch: {
    value (newVal) {
      if (newVal) {
        this.resetForm()
      }
    },
    validateOnly () {
      this.validationResult = null
    }
  },
  methods: {
    async onFileSelected (file) {
      if (file) {
        try {
          const text = await this.readFileAsText(file)
          this.xmlContent = text
          this.validationResult = null
        } catch (error) {
          console.error('Error reading file:', error)
          this.$emit('error', 'Greška pri čitanju datoteke')
        }
      } else {
        this.xmlContent = ''
        this.validationResult = null
      }
    },

    readFileAsText (file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => resolve(e.target.result)
        reader.onerror = (e) => reject(e.target.error)
        reader.readAsText(file)
      })
    },

    async sendDocument () {
      if (!this.$refs.form.validate()) {
        return
      }

      this.loading = true
      this.validationResult = null

      try {
        const payload = {
          xmlContent: this.xmlContent,
          documentId: this.documentId || undefined,
          notes: this.notes || undefined
        }

        if (this.validateOnly) {
          this.validationResult = { success: true, message: 'Dokument je uspješno validiran' }
        }

        this.$emit('send-document', {
          payload,
          validate: this.validateOnly
        })

        if (!this.validateOnly) {
          this.dialog = false
        }
      } catch (error) {
        console.error('Error sending document:', error)
        this.validationResult = {
          success: false,
          message: error.message || 'Neočekivana greška'
        }
      } finally {
        this.loading = false
      }
    },

    resetForm () {
      this.selectedFile = null
      this.xmlContent = ''
      this.documentId = ''
      this.notes = ''
      this.validateOnly = false
      this.validationResult = null
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.resetValidation()
        }
      })
    }
  }
}
</script>

<style scoped>
.v-textarea >>> textarea {
  font-family: 'Courier New', monospace;
  font-size: 12px;
}
</style>
